import { gql, useMutation } from '@apollo/client';
import { useAuth } from '@customer-frontend/auth';
import {
  NotEligibleQuizOutcome,
  ReopenApplicationMutation,
  ReopenApplicationMutationVariables,
} from '@customer-frontend/graphql-types';
import { useQuizApplication } from '@customer-frontend/services';
import {
  Button,
  ButtonPalette,
  Card,
  LoadingSpinner,
  Typography,
  useNotification,
} from '@eucalyptusvc/design-system';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { RejectedReasonWithExplanation, useRejectReasons } from './utils';
import { getConfig } from '@customer-frontend/config';
import { useShowZendeskWidget } from '@customer-frontend/consultation';
import {
  useFeatureFlagBoolean,
  useFeatureFlagClient,
} from '@customer-frontend/feature-flags';

interface WeightRejectedProps {
  quizApplicationId: string;
  profileRoute: string;
  buttonPallete?: ButtonPalette;
}

export const WeightRejected = ({
  quizApplicationId,
  profileRoute,
  buttonPallete = 'default',
}: WeightRejectedProps): React.ReactElement => {
  const { title, countryCode, rejectionContactLink } = getConfig();
  const history = useHistory();
  const { formatMessage } = useIntl();
  const notify = useNotification();
  const featureFlagClient = useFeatureFlagClient();
  const xpReopenApplicationEnabled =
    featureFlagClient.getMultivariate('XP_REOPEN_QUIZ') === 'variation';
  const ffEnableSimpleWeightRejectionLetter = useFeatureFlagBoolean(
    'FF_ENABLE_SIMPLE_WEIGHT_REJECTION_LETTER',
  );

  const [reopenApplication, { loading: loadingReopenApplication }] =
    useMutation<ReopenApplicationMutation, ReopenApplicationMutationVariables>(
      gql`
        mutation ReopenApplication($id: String!) {
          reopenApplication(id: $id) {
            id
          }
        }
      `,
      {
        variables: {
          id: quizApplicationId,
        },
        onError: () => {
          notify.error({
            message: formatMessage({
              defaultMessage: 'Unable to reopen quiz',
            }),
          });
        },
      },
    );

  const { data: quizApplicationData, loading: quizApplicationLoading } =
    useQuizApplication(quizApplicationId);

  const rejectReasons = useRejectReasons();

  const { loggedInUser } = useAuth();

  useShowZendeskWidget({
    problemType: quizApplicationData?.getQuizApplication?.problemType,
  });

  const handleReopenApplication = async (): Promise<void> => {
    await reopenApplication({ variables: { id: quizApplicationId } });
    return history.push(`/quiz/${quizApplicationId}?restart=true`);
  };

  if (quizApplicationLoading) {
    return (
      <div className="flex justify-center p-5 text-primary-500">
        <LoadingSpinner />
      </div>
    );
  }

  const ineligibleOutcome =
    quizApplicationData?.getQuizApplication?.outcomes?.find(
      (o): o is NotEligibleQuizOutcome =>
        o.__typename === 'NotEligibleQuizOutcome',
    );

  let reasonsForIneligibility: RejectedReasonWithExplanation[] = [];

  if (ineligibleOutcome) {
    const reasons = rejectReasons.filter((item) =>
      ineligibleOutcome.reasons?.includes(item.reason),
    );
    reasonsForIneligibility = reasons;
  }

  return (
    <div className="flex flex-col gap-6">
      <Typography size="xl" element="h1" isBold>
        <FormattedMessage
          defaultMessage="{firstName}, thank you for your answers"
          values={{ firstName: loggedInUser?.shortAddressableName }}
        />
      </Typography>
      <Card>
        {ffEnableSimpleWeightRejectionLetter ? (
          xpReopenApplicationEnabled ? (
            <div className="space-y-4">
              <Typography size="medium-paragraph">
                <FormattedMessage
                  defaultMessage="Your health is our top priority.{br}{br}Based on your responses to our initial questionnaire, the treatments available with {brand} appear unlikely to be suitable for you under our clinical guidelines. These guidelines are in place to ensure we provide safe and effective care for everyone.{br}{br}<b>If you think you might have made an error in filling out your responses, click the 'Restart Quiz' button below to fill out your questionnaire again.</b>{br}{br}<i>By clicking 'Restart Quiz', I consent to only provide truthful and accurate responses. I understand falsifying information puts my health and safety at risk.</i>{br}{br}If you would like to speak with our care team and discuss your options, you <a>can contact us here.</a>"
                  values={{
                    br: <br />,
                    b: (chunks) => <strong>{chunks}</strong>,
                    i: (chunks) => <i>{chunks}</i>,
                    brand: title,
                    a: (str) => (
                      <a
                        className="text-link"
                        target="_blank"
                        rel="noreferrer"
                        href={rejectionContactLink}
                      >
                        {str}
                      </a>
                    ),
                  }}
                />
              </Typography>
              <Button
                palette={buttonPallete}
                isFullWidth
                onClick={handleReopenApplication}
                isLoading={loadingReopenApplication}
              >
                <FormattedMessage defaultMessage="Restart Quiz" />
              </Button>
            </div>
          ) : (
            <Typography size="medium-paragraph">
              <FormattedMessage
                defaultMessage="Your health is our top priority.{br}{br} Based on your responses to our initial questionnaire, we’re unable to offer a {brand} treatment that’s clinically appropriate under our prescribing guidelines. These guidelines are in place to ensure we provide safe and effective care for everyone.{br}{br} We understand this may be disappointing. If you would like to speak with our care team or explore other options, you <a>can contact us here.</a>"
                values={{
                  brand: title,
                  br: <br />,
                  a: (str) => (
                    <a
                      className="text-link"
                      target="_blank"
                      rel="noreferrer"
                      href={rejectionContactLink}
                    >
                      {str}
                    </a>
                  ),
                }}
              />
            </Typography>
          )
        ) : (
          <>
            <div className="mb-3">
              <Typography size="medium-paragraph">
                <FormattedMessage
                  defaultMessage="Your health will always be our top priority. Based on your responses, <strong>{brand, select, Compound {Compound’s Body Transformation} other {{brand}'s Weight Reset}} {isGb, select, true {Programme} other {Program}}</strong> is unlikely to be suitable for you. Here's why:"
                  values={{
                    strong: (chunks) => (
                      <strong className="font-strong">{chunks}</strong>
                    ),
                    brand: title,
                    isGb: countryCode === 'GB',
                  }}
                />
              </Typography>
            </div>
            <ul>
              {reasonsForIneligibility.map((item) => (
                <li key={item.heading} className="mt-2">
                  <Typography isBold size="sm">
                    {item.heading}
                  </Typography>
                  {item.paragraphs.map((paragraph) => (
                    <div className="mt-2" key={paragraph?.toString()}>
                      <Typography size="medium-paragraph">
                        {paragraph}
                      </Typography>
                    </div>
                  ))}
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>
      <div className="space-y-4 mt-8">
        <Button
          palette={buttonPallete}
          isFullWidth
          onClick={() => history.push(profileRoute)}
        >
          <FormattedMessage defaultMessage="Back home" />
        </Button>
      </div>
    </div>
  );
};
